import { Box, Stack, Tooltip, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { ValveGlyph } from './EmptyList'
import useTranslation from '@/hooks/useTranslation'
import {
  VALVE_GROUP_LABEL_KEYS,
  VALVE_GROUP_PUMP,
  VALVE_TYPE_LABEL_KEYS,
  VALVE_TYPE_TIP_KEYS,
  valveGroupOf,
} from './constants'
import { useRuleCopy } from './use-rule-copy'
import { errorCodeName } from './error-codes'

/** A pump: impeller housing with an inlet and a discharge. */
const PumpGlyph = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 20, height: 20 }}>
    <circle
      cx="12"
      cy="13"
      r="5.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M12 13 L12 7.8 M12 13 L16.5 15.6 M12 13 L7.5 15.6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M3 13 h3.8 M12 5.5 V3.5 h4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </Box>
)

export interface ValveCardRow {
  id: number
  active: boolean
  /** SRC's flow valve type as the stored number; labels are derived. */
  type: number
  io: string
  decoderRef: string
  errorCode: number
  errorName: string
  flowSensor: string
  capacity: number
  priority: number
  startDelay: number
  stopDelay: number
  /** Tolgee keys, not text. */
  stateKey: string
  errorKey: string
}

interface ValveCardsProps {
  rows: ValveCardRow[]
  isDark: boolean
  /** Metric l/m — the share of the pipe each valve can carry. */
  pipeCapacity?: number
  /** Converts stored metric capacity for display. */
  formatCapacity: (value: number) => string
  capacityUnit: string
}

/** Keys, matching VALVE_STATES — never the rendered label. */
const OPEN_STATE_KEYS = new Set(['commonActionsOpen'])

/**
 * The valves and pumps as cards rather than table rows.
 *
 * Three bands, in the order the questions get asked: what is it and is it
 * running; what are its numbers; and is anything wrong. The numbers sit in one
 * measure row so they line up across cards and can be compared down a column
 * — which is the one thing a card layout usually gives up against a table, and
 * the reason priority and the delays live there rather than as loose text.
 *
 * Cards are ordered by priority, matching the order the controller starts them
 * (Controller UI v230 §5.9.3.5), so reading down is reading the start order.
 */
const ValveCards = ({
  rows,
  isDark,
  pipeCapacity,
  formatCapacity,
  capacityUnit,
}: ValveCardsProps) => {
  const copy = useRuleCopy()
  const { t } = useTranslation()
  const typeLabel = (type: number) => t(VALVE_TYPE_LABEL_KEYS[type] ?? '')
  const typeTip = (type: number) => t(VALVE_TYPE_TIP_KEYS[type] ?? '')
  const groupLabel = (type: number) => t(VALVE_GROUP_LABEL_KEYS[valveGroupOf(type)])
  const ordered = [...rows].sort(
    (a, b) => Number(b.priority ?? 0) - Number(a.priority ?? 0),
  )

  const ink = isDark ? '#eaf1f3' : '#1d2939'
  const muted = isDark ? '#8fa3ad' : '#667085'
  const line = isDark ? 'rgba(255,255,255,0.09)' : '#e4eaea'
  const surface = isDark ? '#0a2836' : '#ffffff'
  const wellBg = isDark ? 'rgba(255,255,255,0.035)' : '#f7fafa'
  const accentOpen = isDark ? '#34d399' : '#0f9d58'
  const accentIdle = isDark ? '#5b7683' : '#98a5ad'
  const danger = isDark ? '#f87171' : '#d32f2f'
  const warn = isDark ? '#fbbf24' : '#b45309'
  const good = isDark ? '#5fe0ad' : '#0f9d58'

  /** One figure in the measure row: small label above, value below. */
  const measure = (
    label: string,
    value: React.ReactNode,
    tip: string,
    emphasis = false,
  ) => (
    <Tooltip title={tip} placement="top">
      <Stack sx={{ gap: '1px', minWidth: 0, flex: 1, cursor: 'help' }}>
        <Typography
          sx={{
            fontSize: '9.5px',
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: muted,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: emphasis ? '19px' : '14px',
            fontWeight: emphasis ? 700 : 600,
            lineHeight: 1.15,
            color: ink,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      </Stack>
    </Tooltip>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: '10px',
        p: '12px',
      }}
    >
      {ordered.map((row) => {
        const isPump = valveGroupOf(row.type) === VALVE_GROUP_PUMP
        const isOpen = OPEN_STATE_KEYS.has(row.stateKey)
        const hasError =
          Boolean(row.errorKey) && row.errorKey !== 'flowRulePanelNone'
        const unprotected = row.flowSensor === copy.noFlowSensorNone
        const accent = hasError ? danger : isOpen ? accentOpen : accentIdle
        const share =
          pipeCapacity && pipeCapacity > 0
            ? Math.round((Number(row.capacity ?? 0) / pipeCapacity) * 100)
            : null
        const errorName = row.errorName || errorCodeName(row.errorCode)

        return (
          <Box
            key={row.id}
            sx={{
              position: 'relative',
              flex: '1 1 300px',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: line,
              borderRadius: '12px',
              backgroundColor: surface,
              overflow: 'hidden',
              opacity: row.active ? 1 : 0.6,
              transition: 'box-shadow 140ms ease',
              '&:hover': {
                boxShadow: isDark
                  ? '0 6px 18px rgba(0,0,0,0.45)'
                  : '0 6px 18px rgba(16,24,40,0.10)',
              },
            }}
          >
            {/* State rail: a wall of cards reads as running / not running
                before any word is read. */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '3px',
                backgroundColor: accent,
              }}
            />

            {/* 1 — identity and state */}
            <Stack
              direction="row"
              sx={{
                alignItems: 'flex-start',
                gap: '9px',
                pl: '14px',
                pr: '11px',
                pt: '11px',
                pb: '10px',
              }}
            >
              <Tooltip title={typeTip(row.type)} placement="top">
                <Box
                  sx={{
                    color: accent,
                    display: 'inline-flex',
                    flexShrink: 0,
                    mt: '1px',
                  }}
                >
                  {isPump ? <PumpGlyph /> : <ValveGlyph />}
                </Box>
              </Tooltip>

              <Stack sx={{ minWidth: 0, flex: 1, gap: '2px' }}>
                <Tooltip title={typeTip(row.type)} placement="top">
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'baseline', gap: '6px', minWidth: 0 }}
                  >
                    <Typography
                      sx={{
                        fontSize: '13.5px',
                        fontWeight: 700,
                        color: ink,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {typeLabel(row.type)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '9.5px',
                        fontWeight: 700,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        color: muted,
                        border: '1px solid',
                        borderColor: line,
                        borderRadius: '4px',
                        px: '4px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {groupLabel(row.type)}
                    </Typography>
                  </Stack>
                </Tooltip>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'baseline', gap: '6px', minWidth: 0 }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: ink,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.io || '—'}
                  </Typography>
                  {row.decoderRef && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: muted,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.decoderRef}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              {/* State as a pill: it is a status, and a pill is how the rest of
                  the app draws one. */}
              <Typography
                sx={{
                  flexShrink: 0,
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: row.active ? accent : muted,
                  backgroundColor: row.active
                    ? `${accent}1f`
                    : isDark
                      ? 'rgba(255,255,255,0.06)'
                      : 'rgba(16,24,40,0.05)',
                  borderRadius: '11px',
                  px: '8px',
                  py: '3px',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.active ? (row.stateKey && t(row.stateKey)) || '—' : copy.sensorNotInUse}
              </Typography>
            </Stack>

            {/* 2 — the numbers, aligned across cards */}
            <Box
              sx={{
                // Two by two rather than four across: at card width four
                // columns crush the labels, and these pair naturally — the
                // ratings on top, the timings beneath.
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: '10px',
                rowGap: '9px',
                mr: '11px',
                ml: '14px',
                px: '10px',
                py: '9px',
                borderRadius: '9px',
                backgroundColor: wellBg,
              }}
            >
              {measure(
                'Capacity',
                <>
                  {formatCapacity(Number(row.capacity ?? 0))}
                  <Box
                    component="span"
                    sx={{ fontSize: '11px', fontWeight: 500, color: muted }}
                  >
                    {' '}
                    {capacityUnit}
                  </Box>
                </>,
                share != null
                  ? `${share}% of the pipe's ${formatCapacity(
                      pipeCapacity as number,
                    )} ${capacityUnit}`
                  : copy.tips.capacity,
                true,
              )}
              {measure(
                'Priority',
                Number(row.priority ?? 0),
                copy.tips.priority,
              )}
              {measure(
                copy.startDelayLabel,
                `${Number(row.startDelay ?? 0)}s`,
                copy.tips.startDelaySeconds,
              )}
              {measure(
                copy.stopDelayLabel,
                `${Number(row.stopDelay ?? 0)}s`,
                copy.tips.stopDelaySeconds,
              )}
            </Box>

            {/* 3 — protection, and anything wrong */}
            <Stack sx={{ gap: '6px', pl: '14px', pr: '11px', py: '10px' }}>
              <Tooltip title={copy.tips.noFlowSensor} placement="top">
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: '5px',
                    color: unprotected ? warn : good,
                    cursor: 'help',
                  }}
                >
                  {unprotected ? (
                    <WarningAmberOutlinedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {unprotected ? copy.noFlowSensorAssigned : row.flowSensor}
                  </Typography>
                </Stack>
              </Tooltip>

              {hasError && (
                // Same shape as the stations list: the state, then
                // "Code: N - name".
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                    gap: '5px',
                    px: '8px',
                    py: '5px',
                    borderRadius: '8px',
                    color: danger,
                    backgroundColor: isDark
                      ? 'rgba(248,113,113,0.14)'
                      : 'rgba(211,47,47,0.08)',
                  }}
                >
                  <WarningAmberOutlinedIcon sx={{ fontSize: 13 }} />
                  <Typography sx={{ fontSize: '11.5px', fontWeight: 700 }}>
                    {t(row.errorKey)}
                  </Typography>
                  {Number(row.errorCode) > 0 && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 500,
                        opacity: 0.9,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      · Code {row.errorCode}
                      {errorName ? ` — ${errorName}` : ''}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        )
      })}
    </Box>
  )
}

export default ValveCards
